/**
 * useAlertBoard — the realtime alert state behind an "alert board" page.
 *
 * Owns everything between the injected `HexAlertsApi` and what a board renders: the camera
 * roster, per-camera alert counters, the live/recording dots, the docked panel's live
 * stream tiles and its paginated card list, and the lifecycle actions. Two pages consume
 * it — AlertHexagonPage (every camera, clustered by site) and CameraMapPage (the cameras
 * pinned on one floor plan) — so the subtleties below are written once:
 *
 *   • an offline transition is debounced but an online one is not,
 *   • an ack that arrives twice (optimistic + echoed socket event) decrements once,
 *   • the newest alert's tile streams persistently to keep the WebRTC transport warm,
 *     and a hidden warmer holds one subscription open when no tile does,
 *   • a CRITICAL alert's tile keeps streaming until the alert is actioned, and the newest
 *     non-critical tile is kept alongside it, so a critical never blacks out the lesser
 *     alert the operator was already watching (see `heldCams`),
 *   • acking the last streaming alert promotes the next active one instead of going dark,
 *   • a node-side wipe (the demo reset's TRUNCATE) arrives as `onCleared` and tears the whole
 *     board down, because a wipe emits no per-alert events to reconcile against.
 *
 * `cameraFilter` scopes what the PANEL shows without touching what is stored: the map page
 * passes the cameras on the selected layout, so switching layout is instant and re-selecting
 * the previous one shows its alerts again — nothing was discarded.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import type { AlertTile } from '../components/hexagon/AlertTileStack';
import type { HexColorState } from '../components/hexagon/types';
import type { DemoResetInfo, HexAlert, HexAlertsApi, HexCamera, HexCounter } from '../api/contracts';
import { useSetDemoReset } from '../api/DemoResetProvider';

/** How long a camera may stay down (connecting/error) before its marker flips offline.
 *  Absorbs brief reconnect blips on otherwise-healthy cameras so they don't flicker.
 *  Online applies immediately: the recording pipeline now reports `streaming` only once
 *  real frames flow (first buffer), so an `up` is trustworthy — a dead camera stuck
 *  restarting never emits it and stays offline. */
const OFFLINE_GRACE_MS = 15000;

/** Statuses that take an alert OFF the board (no longer needs attention). */
const CLEARED_STATUSES = new Set(['acknowledged', 'assigned', 'resolved', 'cancelled']);

/** An alert still shows on the board only while it's untouched ('new' / unknown). */
export const isActiveAlert = (a: HexAlert): boolean => !a.status || !CLEARED_STATUSES.has(a.status);

const emptyCounter = (): HexCounter => ({ critical: 0, warning: 0, normal: 0, total: 0 });

const CARDS_PER_PAGE = 24;

/** Cap on extra pages fetched purely to fill a filtered card list — see the auto-page
 *  effect. Bounds the request burst when a layout's cameras are quiet but the node is not. */
const MAX_AUTO_PAGES = 5;

export interface UseAlertBoardOptions {
  api: HexAlertsApi;
  /** Cloud: no node selected yet — hold every fetch and subscription. */
  blocked?: boolean;
  /** Changes when the selected node changes → reload cameras and re-watch. */
  nodeKey?: string;
  /**
   * Restrict the docked panel (live tiles + cards + the hidden warmer) to these cameras.
   * `null`/omitted shows everything. MUST be referentially stable across renders (memoize
   * it) — it is a dependency of the derived lists.
   */
  cameraFilter?: Set<string> | null;
}

export interface AlertBoard {
  cameras: HexCamera[];
  loading: boolean;
  counters: Record<string, HexCounter>;
  /** Live feed of active alerts, newest first — backs hover popovers. */
  recent: HexAlert[];
  /** Active-alert cards for the panel, already filtered. */
  cards: HexAlert[];
  /** Live stream tiles for the panel, already filtered. */
  liveTiles: AlertTile[];
  /** The tile that streams with no countdown (keeps the transport warm). */
  primaryCameraId: string | null;
  /** Cameras whose live tile must never be closed by a timer — see `heldCams` below. */
  heldCameraIds: Set<string>;
  /** The node's demo wipe cycle, or null on a normal deployment (nothing to show). */
  demoReset: DemoResetInfo | null;
  /** A camera to hold subscribed while no tile exists, or null. */
  warmupCameraId: string | null;
  nameById: Record<string, string>;
  liveFor: (cam: HexCamera) => boolean;
  recordingFor: (cam: HexCamera) => boolean;
  colorStateFor: (cam: HexCamera) => HexColorState;
  counterFor: (cam: HexCamera) => HexCounter | undefined;
  latestAlertFor: (cameraId: string) => HexAlert | undefined;
  handleTileExpire: (cameraId: string) => void;
  handleTileContinue: (cameraId: string) => void;
  handleAcknowledge: (alert: HexAlert) => void;
  handleResolve: (alert: HexAlert) => void;
  loadMoreCards: () => void;
  hasMore: boolean;
  loadingCards: boolean;
}

export function useAlertBoard({ api, blocked = false, nodeKey, cameraFilter = null }: UseAlertBoardOptions): AlertBoard {
  const [cameras, setCameras] = useState<HexCamera[]>([]);
  const [loading, setLoading] = useState(true);
  const [counters, setCounters] = useState<Record<string, HexCounter>>({});
  const [recent, setRecent] = useState<HexAlert[]>([]);
  // Live-stream tiles for the panel's TOP section: cameraId → the alert currently driving
  // its stream. SESSION-ONLY — populated solely by onAlert (a live arrival this session),
  // so a page reload starts empty ⇒ the panel shows CARDS ONLY and never auto-streams every
  // active alert. A preview that elapses is removed here (the alert lives on as a card); a
  // fresh alert re-adds the camera so its stream revives.
  const [streamCams, setStreamCams] = useState<Record<string, HexAlert>>({});
  // Cameras the operator pressed "Continue" on — pinned to the top and kept streaming.
  const [continuedCams, setContinuedCams] = useState<Set<string>>(() => new Set());
  // Paginated active-alert cards for the panel's BOTTOM section (latest-first, infinite scroll).
  const [cards, setCards] = useState<HexAlert[]>([]);
  const [cardPage, setCardPage] = useState(1);
  const [cardTotalPages, setCardTotalPages] = useState(1);
  const [loadingCards, setLoadingCards] = useState(false);
  // Live overrides (cameraId → running) for each status dimension, layered over the
  // loadCameras() snapshot so the LIVE/REC dots track changes after mount.
  const [liveOverrides, setLiveOverrides] = useState<Record<string, boolean>>({});
  const [recordingOverrides, setRecordingOverrides] = useState<Record<string, boolean>>({});
  // Pending offline timers per camera, per dimension, for the debounce below.
  const liveTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const recordingTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // One-shot guard: on (re)load we seed the most-recent active alert's camera into
  // streamCams so its stream starts immediately and warms the WebRTC transport — see the
  // seeding effect below. Reset on node switch so the new node re-seeds.
  const seededRef = useRef(false);
  // Bumped by onCleared (the node's event store was wiped). Nothing about the board's state
  // is salvageable at that point, so it is torn down and the card fetch re-run — which is
  // what this counts for: the fetch effect keys on it, so a reset refetches page 1 even
  // though neither the api nor the node changed.
  const [resetNonce, setResetNonce] = useState(0);
  // The node's demo wipe cycle, once it announces one. Null on every normal deployment.
  // Also published app-wide: the title-bar banner is the thing that renders it (the wipe
  // clears attendance and reports too, so the notice is not the board's to own), and this
  // hook's socket is where the announcement arrives. `publishDemoReset` is a no-op when the
  // app mounted no <DemoResetProvider>, so nothing here depends on one existing.
  const [demoReset, setDemoReset] = useState<DemoResetInfo | null>(null);
  const publishDemoReset = useSetDemoReset();
  // Alert ids already cleared this session. An ack fires clearAlert twice — once
  // optimistically and once via the echoed alert:lifecycle socket event — so without this
  // guard the per-camera counter would be decremented twice (count dropped by 2 per ack).
  const clearedAlertIdsRef = useRef<Set<string>>(new Set());

  // Apply a running/offline update with a debounced OFFLINE transition. A dimension goes
  // running immediately, but a drop to offline (the pipeline reporting connecting/error) is
  // held for OFFLINE_GRACE_MS: a healthy camera that briefly dips between streaming
  // stretches keeps its dot green instead of flickering, while one that stays down past the
  // window (e.g. a camera stuck reconnecting) settles to offline.
  const applyStatus = useCallback(
    (
      setOverrides: Dispatch<SetStateAction<Record<string, boolean>>>,
      timers: Map<string, ReturnType<typeof setTimeout>>,
      update: Record<string, boolean>,
    ) => {
      for (const [id, isUp] of Object.entries(update)) {
        const pending = timers.get(id);
        if (isUp) {
          if (pending) {
            clearTimeout(pending);
            timers.delete(id);
          }
          setOverrides((prev) => (prev[id] === true ? prev : { ...prev, [id]: true }));
        } else if (!pending) {
          // Don't restart the timer on repeated offline events (connecting fires every ~2s).
          timers.set(
            id,
            setTimeout(() => {
              timers.delete(id);
              setOverrides((prev) => ({ ...prev, [id]: false }));
            }, OFFLINE_GRACE_MS),
          );
        }
      }
    },
    [],
  );

  const onLiveStatus = useCallback(
    (live: Record<string, boolean>) => applyStatus(setLiveOverrides, liveTimers.current, live),
    [applyStatus],
  );
  const onRecordingStatus = useCallback(
    (rec: Record<string, boolean>) => applyStatus(setRecordingOverrides, recordingTimers.current, rec),
    [applyStatus],
  );

  // Clear all pending offline timers (node switch / unmount).
  const clearStatusTimers = useCallback(() => {
    liveTimers.current.forEach(clearTimeout);
    liveTimers.current.clear();
    recordingTimers.current.forEach(clearTimeout);
    recordingTimers.current.clear();
  }, []);

  // Remove a cleared alert from the board: drop it from the recent feed (tiles + hover)
  // and revert its contribution to the camera's colour. Used by live lifecycle events
  // and by optimistic ack/resolve from the alert tiles.
  const clearAlert = useCallback((alertId?: string, cameraId?: string, severity?: HexAlert['severity']) => {
    // Idempotent per alert id: the optimistic ack and the echoed lifecycle event both call
    // this for the same alert; only the first should mutate the feed/cards/counters.
    if (alertId) {
      if (clearedAlertIdsRef.current.has(alertId)) return;
      clearedAlertIdsRef.current.add(alertId);
    }
    setRecent((prev) =>
      prev.filter((a) => (alertId ? a.id !== alertId : cameraId ? a.cameraId !== cameraId : true)),
    );
    // Drop the card for this specific alert (or all of a camera's cards if no id given).
    setCards((prev) =>
      prev.filter((c) => (alertId ? c.id !== alertId : cameraId ? c.cameraId !== cameraId : true)),
    );
    if (!cameraId) return;
    // A cleared (ack'd/resolved) camera no longer needs a live stream tile.
    setStreamCams((prev) => {
      if (!(cameraId in prev)) return prev;
      const next = { ...prev };
      delete next[cameraId];
      return next;
    });
    setContinuedCams((prev) => {
      if (!prev.has(cameraId)) return prev;
      const next = new Set(prev);
      next.delete(cameraId);
      return next;
    });
    setCounters((prev) => {
      const cur = prev[cameraId];
      if (!cur) return prev;
      const sev = severity || 'normal';
      const next: HexCounter = {
        ...cur,
        total: Math.max(0, cur.total - 1),
        [sev]: Math.max(0, cur[sev] - 1),
      };
      return { ...prev, [cameraId]: next };
    });
  }, []);

  // Load cameras (on mount and when the node changes).
  useEffect(() => {
    if (blocked) {
      setCameras([]);
      setLoading(false);
      return;
    }
    let mounted = true;
    setLoading(true);
    api
      .loadCameras()
      .then((cams) => mounted && setCameras(cams))
      .catch(() => mounted && setCameras([]))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [api, nodeKey, blocked]);

  // Subscribe to alert counters + recent feed.
  useEffect(() => {
    if (blocked) return;
    setCounters({});
    setRecent([]);
    setLiveOverrides({});
    setRecordingOverrides({});
    setStreamCams({});
    setContinuedCams(new Set());
    // Cleared LOCALLY only — deliberately NOT published.
    //
    // This effect re-runs whenever the board (re)subscribes, which includes simply mounting
    // it again: navigating between two pages of the SAME site tore the title-bar banner down
    // and left it down until the next announcement arrived. The banner is app-scoped, and the
    // only event that should retract it is the SCOPE changing — which the app knows and this
    // hook does not (the cloud clears it on selectedNodeId in its header; the local node has
    // one site, so nothing to clear). A board remount is not a scope change.
    setDemoReset(null);
    seededRef.current = false;
    clearedAlertIdsRef.current = new Set();
    clearStatusTimers();
    const stop = api.watch({
      onSnapshot: (c, r) => {
        setCounters(c);
        setRecent(r.filter(isActiveAlert));
      },
      onLiveStatus,
      onRecordingStatus,
      onAlert: (a) => {
        if (!isActiveAlert(a)) return;
        // A live arrival (THIS session) starts/refreshes a stream tile at the top — and
        // revives a camera whose previous preview had expired.
        if (a.cameraId) setStreamCams((prev) => ({ ...prev, [a.cameraId]: a }));
        // It also enters the active-alert card list at the top (dedupe by id).
        setCards((prev) => (a.id && prev.some((c) => c.id === a.id) ? prev : [a, ...prev]));
        setCounters((prev) => {
          const cur = { ...(prev[a.cameraId] || emptyCounter()) };
          cur.total += 1;
          cur[a.severity] += 1;
          return { ...prev, [a.cameraId]: cur };
        });
        setRecent((prev) => [a, ...prev.filter((p) => !a.id || p.id !== a.id)].slice(0, 30));
      },
      onAlertPreview: (p) => {
        if (!p.alertId) return;
        // Patch in place — the alert is already on screen (live tile + card + feed) and only
        // its image is new. Bumping `previewReady` is what re-triggers AlertCard's resolver;
        // `snapshotUrl` is filled only by hosts that can hand over a plain URL.
        const patch = (a: HexAlert): HexAlert =>
          a.id === p.alertId
            ? { ...a, snapshotUrl: p.snapshotUrl ?? a.snapshotUrl, previewReady: (a.previewReady || 0) + 1 }
            : a;
        setRecent((prev) => prev.map(patch));
        setCards((prev) => prev.map(patch));
        setStreamCams((prev) => {
          const hit = Object.entries(prev).find(([, a]) => a?.id === p.alertId);
          return hit ? { ...prev, [hit[0]]: patch(hit[1]) } : prev;
        });
      },
      onLifecycle: (c) => {
        // Any non-'new' transition takes the alert off the board.
        if (c.status && CLEARED_STATUSES.has(c.status)) clearAlert(c.alertId, c.cameraId, c.severity);
      },
      onCleared: () => {
        // The node's event tables were truncated: every count, card, tile and dedupe id here
        // refers to a row that is gone. Tear the whole lot down rather than reconciling —
        // there is nothing to reconcile against — and let the adapter's follow-up snapshot
        // plus the refetched first page repaint whatever is genuinely still active.
        setCounters({});
        setRecent([]);
        setCards([]);
        setCardPage(1);
        setCardTotalPages(1);
        setStreamCams({});
        setContinuedCams(new Set());
        clearedAlertIdsRef.current = new Set();
        // Re-arm the warm-up seed: this is a fresh start, so the next active alert should
        // get its stream opened the way it would on a load.
        seededRef.current = false;
        setResetNonce((n) => n + 1);
      },
      onDemoSchedule: (info) => {
        // Kept only while the cycle is actually on, so `demoReset` reads as "there is a wipe
        // coming" and no consumer has to re-check the flag. A node that switches it off
        // announces enabled:false, which is what takes the banner down without a reload.
        setDemoReset(info?.enabled ? info : null);
        publishDemoReset(info);
      },
    });
    return () => {
      stop();
      clearStatusTimers();
    };
  }, [api, nodeKey, blocked, clearAlert, onLiveStatus, onRecordingStatus, clearStatusTimers]);

  // WARM-UP ON LOAD: stream the most-recent active alert's camera immediately (once per
  // mount / node switch). Its subscription establishes the WebRTC transport (ICE/DTLS)
  // right away, so when a NEW alert arrives the SFU only renegotiates a track over the
  // already-warm connection — no cold signaling handshake, the preview starts instantly.
  // This camera becomes the "primary" tile (streams with no countdown) until a newer alert
  // supersedes it; the guard keeps live arrivals this session from being clobbered.
  useEffect(() => {
    if (seededRef.current || blocked) return;
    const latest = recent.find(isActiveAlert);
    if (!latest?.cameraId) return;
    seededRef.current = true;
    setStreamCams((prev) => (Object.keys(prev).length > 0 ? prev : { [latest.cameraId]: latest }));
  }, [recent, blocked]);

  // Paginated active-alert cards (bottom section). Fetch page 1 on mount / node switch;
  // loadMoreCards() appends subsequent pages as the panel scrolls. Falls back to the
  // realtime feed only if the adapter doesn't implement listActiveAlerts.
  useEffect(() => {
    if (blocked || !api.listActiveAlerts) {
      setCards([]);
      setCardPage(1);
      setCardTotalPages(1);
      return;
    }
    let mounted = true;
    setLoadingCards(true);
    setCards([]);
    setCardPage(1);
    api
      .listActiveAlerts({ page: 1, limit: CARDS_PER_PAGE })
      .then((res) => {
        if (!mounted) return;
        setCards(res.data.filter(isActiveAlert));
        setCardPage(res.page || 1);
        setCardTotalPages(res.totalPages || 1);
      })
      .catch(() => mounted && setCardTotalPages(1))
      .finally(() => mounted && setLoadingCards(false));
    return () => {
      mounted = false;
    };
  }, [api, nodeKey, blocked, resetNonce]);

  const hasMore = cardPage < cardTotalPages;

  const loadMoreCards = useCallback(() => {
    if (!api.listActiveAlerts || loadingCards || cardPage >= cardTotalPages) return;
    const next = cardPage + 1;
    setLoadingCards(true);
    api
      .listActiveAlerts({ page: next, limit: CARDS_PER_PAGE })
      .then((res) => {
        setCards((prev) => {
          const seen = new Set(prev.map((c) => c.id));
          const fresh = res.data.filter((a) => isActiveAlert(a) && (!a.id || !seen.has(a.id)));
          return [...prev, ...fresh];
        });
        setCardPage(res.page || next);
        setCardTotalPages(res.totalPages || cardTotalPages);
      })
      .catch(() => {})
      .finally(() => setLoadingCards(false));
  }, [api, loadingCards, cardPage, cardTotalPages]);

  const liveFor = useCallback(
    (cam: HexCamera): boolean => liveOverrides[cam.id] ?? cam.live,
    [liveOverrides],
  );
  const recordingFor = useCallback(
    (cam: HexCamera): boolean => recordingOverrides[cam.id] ?? cam.recording,
    [recordingOverrides],
  );

  const colorStateFor = useCallback(
    (cam: HexCamera): HexColorState => {
      const c = counters[cam.id];
      if (c) {
        if (c.critical > 0) return 'red';
        if (c.warning > 0) return 'amber';
        if (c.normal > 0) return 'blue';
      }
      // Base colour: online if EITHER live or recording is running.
      return liveFor(cam) || recordingFor(cam) ? 'grey' : 'offline';
    },
    [counters, liveFor, recordingFor],
  );

  const counterFor = useCallback((cam: HexCamera): HexCounter | undefined => counters[cam.id], [counters]);

  const nameById = useMemo(() => {
    const m: Record<string, string> = {};
    cameras.forEach((c) => (m[c.id] = c.name));
    return m;
  }, [cameras]);

  const latestAlertFor = useCallback(
    (camId: string): HexAlert | undefined => recent.find((a) => a.cameraId === camId && isActiveAlert(a)),
    [recent],
  );

  /** In scope for the panel? Always true when no filter is set. */
  const inScope = useCallback(
    (cameraId?: string) => !cameraFilter || (!!cameraId && cameraFilter.has(cameraId)),
    [cameraFilter],
  );

  // HELD tiles: cameras whose live stream is exempt from every closing countdown in the
  // tile stack. Two kinds, and both are here rather than in the tile because only the board
  // can see the whole set:
  //
  //   • EVERY camera with a CRITICAL alert — held until the alert is acknowledged, assigned
  //     or resolved (clearing it drops the tile through the normal cleared path).
  //   • the single most-recent NON-critical camera — the alert an operator is most likely to
  //     be mid-way through reading. Without it a critical arriving on another camera would
  //     take the primary slot and close this one 60s later, so the lesser alert the operator
  //     was actually watching went black the moment a critical landed elsewhere.
  //
  // Everything older than that still winds down on PREVIEW_MS as before, so the number of
  // concurrent streams stays bounded by the criticals outstanding plus one.
  const heldCams = useMemo(() => {
    const held = new Set<string>();
    let newest: { cameraId: string; ts: number } | null = null;
    for (const [camId, a] of Object.entries(streamCams)) {
      if (!a || !isActiveAlert(a) || !inScope(camId)) continue;
      if (a.severity === 'critical') {
        held.add(camId);
        continue;
      }
      const ts = a.timestamp || 0;
      if (!newest || ts > newest.ts) newest = { cameraId: camId, ts };
    }
    if (newest) held.add(newest.cameraId);
    return held;
  }, [streamCams, inScope]);

  // TOP "Live now" tiles: one per camera that alerted THIS session (from `streamCams`),
  // CONTINUED (pinned) first, then HELD, then most-recent alert first. Keyed by cameraId
  // (stable), so a re-alerting camera reorders without remounting — its player keeps playing.
  //
  // Held tiles rank above the plain recency order because they never auto-close — leaving
  // them in recency order would let a run of lesser alerts push one past the 8-tile cut and
  // silently close a stream we promised to keep open.
  const liveTiles: AlertTile[] = useMemo(() => {
    const rank = (t: { cameraId: string; alert: HexAlert }) =>
      continuedCams.has(t.cameraId) ? 2 : heldCams.has(t.cameraId) ? 1 : 0;
    const out = Object.entries(streamCams)
      .filter(([camId, a]) => isActiveAlert(a) && inScope(camId))
      .map(([camId, a]) => ({ cameraId: camId, name: nameById[camId] || camId, alert: a }));
    out.sort((x, y) => {
      const rx = rank(x);
      const ry = rank(y);
      if (rx !== ry) return ry - rx;
      return (y.alert.timestamp || 0) - (x.alert.timestamp || 0);
    });
    return out.slice(0, 8);
  }, [streamCams, continuedCams, heldCams, nameById, inScope]);

  // The PRIMARY tile: the most-recent live tile not pinned via "Continue". It streams
  // persistently (no auto-stop) to keep the connection warm; when a newer alert arrives
  // that one becomes primary and this moves down + starts its closing timer.
  //
  // Chosen by TIMESTAMP, not by position in `liveTiles` — that list now ranks held tiles
  // above lesser alerts, and reading position 0 would hand "primary" to an older critical and
  // put the alert that just arrived on a closing timer with nothing having superseded it.
  const primaryCameraId = useMemo(() => {
    let best: AlertTile | null = null;
    for (const t of liveTiles) {
      if (continuedCams.has(t.cameraId)) continue;
      if (!best || (t.alert.timestamp || 0) > (best.alert.timestamp || 0)) best = t;
    }
    return best?.cameraId ?? null;
  }, [liveTiles, continuedCams]);

  // TRANSPORT WARMER: the WS+PC is kept warm on mount, but the WebRTC transport
  // (ICE/DTLS) isn't actually negotiated until the SFU offers, and the SFU only
  // offers on the first subscribe. So an idle page with no live tiles leaves the
  // transport COLD — the next alert pays the full ICE/TURN/DTLS handshake over the
  // slow tunnel. To avoid that we keep ONE online camera subscribed via a hidden
  // player whenever there are no visible tiles: it forces the SFU offer and gets the
  // transport to 'connected' ahead of time, so a real alert is just a fast track
  // renegotiation. Real tiles keep the transport hot, so the warmer only runs while
  // none exist (bounded to a single extra stream).
  const warmupCameraId = useMemo(() => {
    if (liveTiles.length > 0) return null;
    const online = cameras.find((c) => liveFor(c) && inScope(c.id));
    return online?.id ?? null;
  }, [liveTiles, cameras, liveFor, inScope]);

  // BOTTOM cards: EVERY active alert — INCLUDING any alert currently shown as a live tile
  // above. So the latest alert's snapshot-preview card appears in this list the instant the
  // alert is processed, in parallel with its live-stream tile, instead of only surfacing
  // once a newer alert supersedes it and its preview window elapses. The live tile still
  // demotes + runs its 10s closing timer as before; when it expires the tile simply drops
  // away and its card (already present here) stays in place — no pop-in.
  const visibleCards = useMemo(
    () => cards.filter((c) => isActiveAlert(c) && inScope(c.cameraId)),
    [cards, inScope],
  );

  // AUTO-PAGE WHEN FILTERED: listActiveAlerts pages over the WHOLE node, so on a busy node
  // a layout's quiet cameras can be absent from page 1 entirely and the panel would show
  // an empty list beside a plan that plainly has alerts on it. Pull further pages until
  // something in scope turns up. Bounded, and reset whenever the filter or node changes so
  // a new layout gets its own budget.
  const autoPagesRef = useRef(0);
  useEffect(() => {
    autoPagesRef.current = 0;
  }, [cameraFilter, nodeKey]);
  useEffect(() => {
    if (!cameraFilter || blocked) return;
    // A filter with nothing in it — a layout with no cameras placed on it yet, which is what an
    // untouched map page is — can never match an alert, so no number of further pages would put
    // a card on screen. Reading the node's whole alert history to prove that is not worth one
    // request, let alone the budget below.
    if (cameraFilter.size === 0) return;
    if (visibleCards.length > 0 || !hasMore || loadingCards) return;
    if (autoPagesRef.current >= MAX_AUTO_PAGES) return;
    autoPagesRef.current += 1;
    loadMoreCards();
  }, [cameraFilter, blocked, visibleCards.length, hasMore, loadingCards, loadMoreCards]);

  // A live tile's window elapsed without "Continue": stop the stream (drop the tile) but
  // keep the alert as a card below. A later alert for the camera revives it (see onAlert).
  const handleTileExpire = useCallback((cameraId: string) => {
    setStreamCams((prev) => {
      if (!(cameraId in prev)) return prev;
      const next = { ...prev };
      delete next[cameraId];
      return next;
    });
    setContinuedCams((prev) => {
      if (!prev.has(cameraId)) return prev;
      const next = new Set(prev);
      next.delete(cameraId);
      return next;
    });
  }, []);

  const handleTileContinue = useCallback((cameraId: string) => {
    setContinuedCams((prev) => (prev.has(cameraId) ? prev : new Set(prev).add(cameraId)));
  }, []);

  // Clear an alert AND, if that leaves no live (non-continued) stream running, promote the
  // next most-recent active alert into a live tile. Without this, acking the primary/last
  // alert closed its stream and left the panel with no live feed even though the second-last
  // alert was still active (it lingered only as a snapshot card) — this streams it instead.
  const clearAndPromote = useCallback(
    (alert: HexAlert) => {
      clearAlert(alert.id, alert.cameraId, alert.severity);
      const stillLive = Object.keys(streamCams).some(
        (cid) => cid !== alert.cameraId && !continuedCams.has(cid) && inScope(cid),
      );
      if (stillLive) return;
      // The second-last alert: the most-recent still-active alert (cards are latest-first)
      // whose camera is neither the one just acked nor already streaming. Cards is the
      // authoritative active list, so this holds even after the tile's preview expired.
      const next = cards.find(
        (a) =>
          isActiveAlert(a) &&
          !!a.cameraId &&
          a.cameraId !== alert.cameraId &&
          !(a.cameraId in streamCams) &&
          inScope(a.cameraId),
      );
      if (next?.cameraId) {
        const cam = next.cameraId;
        setStreamCams((prev) => (cam in prev ? prev : { ...prev, [cam]: next }));
      }
    },
    [clearAlert, streamCams, continuedCams, cards, inScope],
  );

  // Lifecycle actions from the tiles/cards. Optimistically take the alert off the board,
  // then call the API; the live lifecycle event / next poll reconciles. A failure leaves
  // the alert cleared locally but the next snapshot/poll will re-surface it if it's still 'new'.
  const handleAcknowledge = useCallback(
    (alert: HexAlert) => {
      if (!api.acknowledgeAlert || !alert.id) return;
      clearAndPromote(alert);
      api.acknowledgeAlert(alert.id).catch(() => {});
    },
    [api, clearAndPromote],
  );
  const handleResolve = useCallback(
    (alert: HexAlert) => {
      if (!api.resolveAlert || !alert.id) return;
      clearAndPromote(alert);
      api.resolveAlert(alert.id).catch(() => {});
    },
    [api, clearAndPromote],
  );

  return {
    cameras,
    loading,
    counters,
    recent,
    cards: visibleCards,
    liveTiles,
    primaryCameraId,
    heldCameraIds: heldCams,
    demoReset,
    warmupCameraId,
    nameById,
    liveFor,
    recordingFor,
    colorStateFor,
    counterFor,
    latestAlertFor,
    handleTileExpire,
    handleTileContinue,
    handleAcknowledge,
    handleResolve,
    loadMoreCards,
    hasMore,
    loadingCards,
  };
}
